// The docking layout (dock.ts) as data: splits of groups of tabs, Unity style. Pure: every function
// returns a new tree and leaves its argument alone. A tab is a panel id; a path is the child indices
// from the root down to a node. Some of a group's tabs can be a stack: shown together as one tab, one
// under another (a stack's tabs are next to each other in tabs, in its order).

export type Group = { kind: 'group'; tabs: string[]; active: string; stacks?: string[][] };
export type Split = { kind: 'split'; dir: 'row' | 'col'; sizes: number[]; children: Node[] };
export type Node = Group | Split;
export type Path = number[];
/** Where a dragged tab lands on a group: in its tabs, a new group beside it on that side, or stacked with the tab in front. */
export type Zone = 'center' | 'left' | 'right' | 'top' | 'bottom' | 'stack';

export const VIEWPORT = 'viewport';

export const group = (tabs: string[], active = tabs[0], stacks?: string[][]): Group =>
  stacks?.length ? { kind: 'group', tabs: [...tabs], active, stacks: stacks.map(x => [...x]) } : { kind: 'group', tabs: [...tabs], active };
export const split = (dir: 'row' | 'col', children: Node[], sizes?: number[]): Split =>
  ({ kind: 'split', dir, children, sizes: sizes ?? children.map(() => 1 / children.length) });

const clone = (n: Node): Node => n.kind === 'group' ? group(n.tabs, n.active, n.stacks)
  : { kind: 'split', dir: n.dir, sizes: [...n.sizes], children: n.children.map(clone) };

/** The tabs shown with tab: its stack, or just it. */
export const stackOf = (g: Group, tab: string): string[] => g.stacks?.find(x => x.includes(tab)) ?? [tab];

/** A group's tabs as its strip shows them: one entry per tab, a stack's together (in its order). */
export function entries(g: Group): string[][] {
  const out: string[][] = [], seen = new Set<string>();
  for (const t of g.tabs) {
    if (seen.has(t)) continue;
    const e = stackOf(g, t).filter(x => g.tabs.includes(x) && !seen.has(x));
    for (const x of e) seen.add(x);
    out.push(e);
  }
  return out;
}

export function at(n: Node, path: Path): Node | null {
  for (const i of path) { if (n.kind !== 'split' || !n.children[i]) return null; n = n.children[i]; }
  return n;
}

/** Every group, with its path, depth first. */
export function groups(n: Node, path: Path = []): { g: Group; path: Path }[] {
  return n.kind === 'group' ? [{ g: n, path }] : n.children.flatMap((c, i) => groups(c, [...path, i]));
}

export const tabs = (n: Node): string[] => groups(n).flatMap(x => x.g.tabs);

/** The path of the group holding tab, or null. */
export const findTab = (n: Node, tab: string): Path | null => groups(n).find(x => x.g.tabs.includes(tab))?.path ?? null;

function parentOf(root: Node, target: Node): { p: Split; i: number } | null {
  if (root.kind !== 'split') return null;
  for (let i = 0; i < root.children.length; i++) {
    if (root.children[i] === target) return { p: root, i };
    const r = parentOf(root.children[i], target);
    if (r) return r;
  }
  return null;
}

/** No empty groups or one-child splits, a split never directly in one of its own direction, sizes summing to 1, no stack of one, a stack's tabs together. */
export function normalize(n: Node): Node {
  if (n.kind === 'group') {
    const t = [...new Set(n.tabs)], seen = new Set<string>(), stacks: string[][] = [];
    for (const x of n.stacks ?? []) {
      const k = [...new Set(x)].filter(y => t.includes(y) && !seen.has(y));
      if (k.length < 2) continue;
      for (const y of k) seen.add(y);
      stacks.push(k);
    }
    const g = group(t, t.includes(n.active) ? n.active : t[0] ?? '', stacks);
    g.tabs = entries(g).flat();
    return g;
  }
  const kids: Node[] = [], sizes: number[] = [];
  n.children.forEach((c, i) => {
    const k = normalize(c), s = n.sizes[i] > 0 ? n.sizes[i] : 0.01;
    if (k.kind === 'group' && !k.tabs.length) return;
    if (k.kind === 'split' && k.dir === n.dir) k.children.forEach((kk, j) => { kids.push(kk); sizes.push(s * k.sizes[j]); });
    else { kids.push(k); sizes.push(s); }
  });
  if (!kids.length) return group([]);
  if (kids.length === 1) return kids[0];
  const sum = sizes.reduce((a, b) => a + b, 0);
  return { kind: 'split', dir: n.dir, children: kids, sizes: sizes.map(s => s / sum) };
}

/** Take tabs out of every group (out of their stacks too); a group's front tab gone, the one after it (or before) is in front. */
function take(r: Node, ids: string[]) {
  for (const { g } of groups(r)) {
    if (!g.tabs.some(x => ids.includes(x))) continue;
    const i = g.tabs.indexOf(g.active), rest = g.tabs.filter(x => !ids.includes(x));
    if (ids.includes(g.active)) g.active = g.tabs.slice(i).find(x => !ids.includes(x)) ?? rest[rest.length - 1] ?? '';
    g.tabs = rest;
    if (g.stacks) g.stacks = g.stacks.map(x => x.filter(y => !ids.includes(y)));
  }
}

export const moveTab = (root: Node, tab: string, target: Path, zone: Zone, before?: string): Node => moveTabs(root, [tab], target, zone, before);

/**
 * Tabs (in the tree or not; more than one: a stack, kept one) into the group at target: among its tabs (before
 * the entry of `before`, else last), in a new group on one side of it, or (stack) stacked with its tab in front
 * (before `before` there, else last). Moving all of a group's tabs beside that same group changes nothing.
 */
export function moveTabs(root: Node, ids: string[], target: Path, zone: Zone, before?: string): Node {
  const r = clone(root), t = at(r, target);
  if (!t || t.kind !== 'group' || !ids.length) return root;
  if (zone !== 'center' && t.tabs.every(x => ids.includes(x))) return root;
  if (zone === 'center' && before && ids.includes(before)) return activate(root, ids[0]);
  if (zone === 'stack') {
    // the tab in front (one of these: another of its stack, else any other)
    const front = [...(t.tabs.includes(t.active) ? stackOf(t, t.active) : []), ...t.tabs].find(x => !ids.includes(x))!;
    const st = stackOf(t, front).filter(x => !ids.includes(x));
    take(r, ids);
    const i = before && st.includes(before) ? st.indexOf(before) : st.length;
    t.stacks = [...(t.stacks ?? []).filter(x => !x.some(y => st.includes(y))), [...st.slice(0, i), ...ids, ...st.slice(i)]];
    t.tabs.push(...ids);
    t.active = front;
    return normalize(r);
  }
  take(r, ids);
  const stack = ids.length > 1 ? [ids] : undefined;
  if (zone === 'center') {
    const i = before ? t.tabs.indexOf(stackOf(t, before)[0]) : -1;
    t.tabs.splice(i < 0 ? t.tabs.length : i, 0, ...ids);
    if (stack) t.stacks = [...(t.stacks ?? []), ...stack];
    t.active = ids[0];
    return normalize(r);
  }
  const dir = zone === 'left' || zone === 'right' ? 'row' : 'col', first = zone === 'left' || zone === 'top';
  const g = group(ids, ids[0], stack), up = parentOf(r, t);
  if (up && up.p.dir === dir) {
    const half = up.p.sizes[up.i] / 2;
    up.p.sizes[up.i] = half;
    up.p.children.splice(first ? up.i : up.i + 1, 0, g);
    up.p.sizes.splice(first ? up.i : up.i + 1, 0, half);
    return normalize(r);
  }
  const s = split(dir, first ? [g, t] : [t, g]);
  if (!up) return normalize(s);
  up.p.children[up.i] = s;
  return normalize(r);
}

export function closeTab(root: Node, tab: string): Node {
  const r = clone(root);
  take(r, [tab]);
  return normalize(r);
}

export function activate(root: Node, tab: string): Node {
  const r = clone(root);
  for (const { g } of groups(r)) if (g.tabs.includes(tab)) g.active = tab;
  return r;
}

/** Move size from child j to child i of the split at path (delta negative: the other way), neither below min. */
export function resize(root: Node, path: Path, i: number, j: number, delta: number, min = 0): Node {
  const r = clone(root), s = at(r, path);
  if (!s || s.kind !== 'split' || !(i in s.sizes) || !(j in s.sizes)) return root;
  const both = s.sizes[i] + s.sizes[j], m = Math.min(min, both / 2);
  s.sizes[i] = Math.max(m, Math.min(both - m, s.sizes[i] + delta));
  s.sizes[j] = both - s.sizes[i];
  return r;
}

/** The biggest group by area (sizes multiplied down the tree): where a tab with no better place goes. */
export function largest(root: Node, skip?: (g: Group) => boolean): Path | null {
  let best: Path | null = null, area = -1;
  const walk = (n: Node, a: number, path: Path) => {
    if (n.kind === 'group') { if (a > area && !skip?.(n)) { area = a; best = path; } return; }
    n.children.forEach((c, i) => walk(c, a * n.sizes[i], [...path, i]));
  };
  walk(root, 1, []);
  return best;
}

/** A saved layout, checked: null unless it is a tree of groups and splits with the viewport in it exactly once. */
export function validate(x: unknown): Node | null {
  const seen = new Set<string>();
  const walk = (n: unknown, depth: number): Node | null => {
    if (!n || typeof n !== 'object' || depth > 32) return null;
    const o = n as Record<string, unknown>;
    if (o.kind === 'group') {
      if (!Array.isArray(o.tabs)) return null;
      const t = o.tabs.filter((s): s is string => typeof s === 'string' && !seen.has(s));
      for (const s of t) seen.add(s);
      // stacks: lists of this group's tabs (a group saved as a list, as this layout once had: all one stack)
      const st = Array.isArray(o.stacks) ? o.stacks.filter(Array.isArray).map(x => (x as unknown[]).filter((y): y is string => typeof y === 'string' && t.includes(y)))
        : o.list === true ? [t] : undefined;
      return group(t, typeof o.active === 'string' && t.includes(o.active) ? o.active : t[0], st);
    }
    if (o.kind === 'split' && (o.dir === 'row' || o.dir === 'col') && Array.isArray(o.children) && o.children.length) {
      const kids = o.children.map(c => walk(c, depth + 1));
      if (kids.some(k => !k)) return null;
      const sz = Array.isArray(o.sizes) && o.sizes.length === kids.length && o.sizes.every(s => typeof s === 'number' && s > 0 && isFinite(s))
        ? o.sizes as number[] : kids.map(() => 1);
      return { kind: 'split', dir: o.dir, children: kids as Node[], sizes: sz };
    }
    return null;
  };
  const r = walk(x, 0);
  return r && seen.has(VIEWPORT) ? normalize(r) : null;
}
